// App.js
import React, { useEffect, useState } from 'react';
// import LearnProgressTab from './LearnProgressTab';
import AdminManagement from './AdminManagement';
import './App.css';
import { useAuth } from './providers/AuthProvider';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from './firebase';

function App() {
  const { user, token, loading: authLoading, loginWithGoogle, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [activities, setActivities] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [displayNameChanges, setDisplayNameChanges] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [recycledFeedback, setRecycledFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(null); // null: unknown, false: not admin, true: admin

  const API_BASE = 'http://localhost:5000';

  useEffect(() => {
    if (!user) return;
    // Check admin status in Firestore
    const checkAdmin = async () => {
      try {
        console.log('Checking admin status for UID:', user.uid);
        const db = getFirestore(app);
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        console.log('Admin doc exists:', adminDoc.exists());
        if (adminDoc.exists()) {
          console.log('Admin doc data:', adminDoc.data());
        }
        setIsAdmin(adminDoc.exists() && adminDoc.data().isAdmin === true);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [user]);

  useEffect(() => {
    if (!token || !isAdmin) return;
    fetchData();
    fetchLeaderboard();
    fetchDisplayNameChanges();
    fetchFeedback();
    fetchRecycledFeedback();
  }, [token, isAdmin]);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [usersRes, analyticsRes, activitiesRes] = await Promise.all([
        fetch(`${API_BASE}/users/combined`, authHeaders()).catch(e => null),
        fetch(`${API_BASE}/analytics/summary`, authHeaders()).catch(e => null),
        fetch(`${API_BASE}/activities/recent`, authHeaders()).catch(e => null)
      ]);

      const usersData = usersRes ? await usersRes.json().catch(() => []) : [];
      const analyticsData = analyticsRes ? await analyticsRes.json().catch(() => ({})) : {};
      const activitiesData = activitiesRes ? await activitiesRes.json().catch(() => []) : [];

      setUsers(usersData);
      setAnalytics(analyticsData);
      setActivities(activitiesData);
      setError((!usersRes && !analyticsRes && !activitiesRes) ? 'Backend server not running. Please start the backend with the Firebase service account key.' : '');
      setLoading(false);
    } catch (err) {
      setError('Backend server not running. Please start the backend with the Firebase service account key.');
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_BASE}/leaderboard`, authHeaders());
      if (!res) {
        setLeaderboard([]);
        return;
      }
      const data = await res.json();
      setLeaderboard(data);
    } catch (err) {
      setLeaderboard([]);
    }
  };

  const fetchDisplayNameChanges = async () => {
    try {
      const res = await fetch(`${API_BASE}/display-name-changes`, authHeaders());
      if (!res) {
        setDisplayNameChanges([]);
        return;
      }
      const data = await res.json();
      console.log('Fetched display name changes:', data); // Debug log
      setDisplayNameChanges(data);
    } catch (err) {
      setDisplayNameChanges([]);
      console.error('Error fetching display name changes:', err); // Debug log
    }
  };

  const fetchFeedback = async () => {
    try {
      const res = await fetch(`${API_BASE}/feedback`, authHeaders());
      if (!res) {
        setFeedback([]);
        return;
      }
      const data = await res.json();
      console.log('Fetched feedback:', data);
      setFeedback(data);
    } catch (err) {
      setFeedback([]);
      console.error('Error fetching feedback:', err);
    }
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      if (newStatus === 'resolved') {
        // Move to recycle bin
        await fetch(`${API_BASE}/feedback/${feedbackId}/resolve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }
        });
      } else {
        await fetch(`${API_BASE}/feedback/${feedbackId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: newStatus })
        });
      }
      fetchFeedback();
      fetchRecycledFeedback();
    } catch (err) {
      console.error('Error updating feedback status:', err);
    }
  };

  const fetchRecycledFeedback = async () => {
    try {
      const res = await fetch(`${API_BASE}/feedback/recycled`, authHeaders());
      const data = await res.json();
      setRecycledFeedback(data);
    } catch (err) {
      console.error('Error fetching recycled feedback:', err);
    }
  };

  const restoreFeedback = async (feedbackId) => {
    try {
      await fetch(`${API_BASE}/feedback/${feedbackId}/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });
      fetchFeedback();
      fetchRecycledFeedback();
    } catch (err) {
      console.error('Error restoring feedback:', err);
    }
  };

  // Robust Firestore timestamp parser
  const formatDate = (ts) => {
    if (!ts) return 'Never';
    try {
      // Firestore Timestamp object
      if (typeof ts === 'object') {
        if (ts.seconds) {
          return new Date(ts.seconds * 1000).toLocaleString();
        }
        if (ts._seconds) {
          return new Date(ts._seconds * 1000).toLocaleString();
        }
        // Firestore Timestamp with toDate method
        if (typeof ts.toDate === 'function') {
          return ts.toDate().toLocaleString();
        }
      }
      // ISO string or number
      const date = new Date(ts);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString();
    } catch (err) {
      console.error('Error formatting date:', err, ts);
      return 'Invalid Date';
    }
  };

  const OverviewTab = () => (
    <div className="tab-content">
      <h2>Analytics Overview</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{analytics.totalUsers || 0}</h3>
          <p>Total Users</p>
        </div>
        <div className="stat-card">
          <h3>{analytics.totalUsersWithProgress || 0}</h3>
          <p>Active Players</p>
        </div>
        <div className="stat-card">
          <h3>{Math.round(analytics.avgLevel || 0)}</h3>
          <p>Average Level</p>
        </div>
        <div className="stat-card">
          <h3>{Math.round(analytics.avgXP || 0)}</h3>
          <p>Average XP</p>
        </div>
        <div className="stat-card">
          <h3>{analytics.maxLevel || 0}</h3>
          <p>Highest Level</p>
        </div>
        <div className="stat-card">
          <h3>{analytics.totalChests || 0}</h3>
          <p>Total Chests Opened</p>
        </div>
      </div>
    </div>
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Successfully copied
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const UsersTab = () => (
    <div className="tab-content">
      <h2>User Management</h2>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Display Name</th>
              <th>Email</th>
              <th>Sign In Method</th>
              <th>UID</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.uid}>
                <td>{user.displayName || 'Not set'}</td>
                <td>{user.email}</td>
                <td>
                  <div className="provider-cell">
                    {user.providerData && user.providerData.length > 0 && user.providerData.some(p => p.providerId === 'google.com') ? (
                      <>
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="provider-logo" />
                        <span className="provider-text">Google</span>
                      </>
                    ) : (
                      <span className="provider-text">Email</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="uid-cell">
                    <span className="uid-text">{user.uid}</span>
                    <button 
                      className="copy-btn" 
                      onClick={() => copyToClipboard(user.uid)}
                      title="Copy UID"
                    >
                      Copy
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const ProgressTab = () => (
    <div className="tab-content">
      <h2>User Progress Details</h2>
      <div className="progress-grid">
        {users.filter(user => user.progress).map(user => (
          <div key={user.uid} className="progress-card">
            <h3>{user.email}</h3>
            <div className="progress-details">
              <div className="progress-row">
                <span>Level:</span>
                <span>{user.progress.level}</span>
              </div>
              <div className="progress-row">
                <span>XP:</span>
                <span>{user.progress.score}</span>
              </div>
              <div className="progress-row">
                <span>User Points:</span>
                <span>{user.progress.userPoints}</span>
              </div>
              <div className="progress-row">
                <span>Claimed Points:</span>
                <span>{user.progress.claimedPoints}/{user.progress.levelGoalPoints}</span>
              </div>
              <div className="progress-row">
                <span>Chests Opened:</span>
                <span>{user.progress.chestsOpened}</span>
              </div>
              <div className="progress-row">
                <span>Current Streak:</span>
                <span>{user.progress.streakDays} days</span>
              </div>
              <div className="progress-row">
                <span>Longest Streak:</span>
                <span>{user.progress.longestStreak} days</span>
              </div>
              <div className="progress-row">
                <span>Achievements:</span>
                <span>{user.progress.achievements?.length || 0}</span>
              </div>
              <div className="progress-row">
                <span>Unlocked Content:</span>
                <span>{user.progress.unlockedContent?.length || 0}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const ActivitiesTab = () => (
    <div className="tab-content">
      <h2>Recent Activities</h2>
      <div className="activities-list">
        {activities.length > 0 ? (
          activities.map(activity => (
            <div key={activity.id} className="activity-item">
              <div className="activity-type">{activity.type}</div>
              <div className="activity-details">{activity.details}</div>
              <div className="activity-user">User: {activity.userId}</div>
              <div className="activity-time">{formatDate(activity.timestamp)}</div>
            </div>
          ))
        ) : (
          <p>No activities found</p>
        )}
      </div>
    </div>
  );

  const LeaderboardTab = () => (
    <div className="tab-content">
      <h2>Global Leaderboard</h2>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Name</th>
              <th>Level</th>
              <th>XP</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((user, idx) => (
              <tr key={user.userId}>
                <td>{idx + 1}</td>
                <td>{user.displayName || user.email?.split('@')[0] || user.userId}</td>
                <td>{user.level}</td>
                <td>{user.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const DisplayNameChangesTab = () => (
    <div className="tab-content">
      <h2>Display Name Changes</h2>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User ID</th>
              <th>Old Name</th>
              <th>New Name</th>
            </tr>
          </thead>
          <tbody>
            {displayNameChanges.map(change => (
              <tr key={change.id}>
                <td>{formatDate(change.timestamp)}</td>
                <td>{change.userId}</td>
                <td>{change.oldName}</td>
                <td>{change.newName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const FeedbackTab = () => (
    <div className="tab-content">
      <h2>User Feedback</h2>
      <div className="feedback-grid">
        {feedback.length > 0 ? (
          feedback.map(item => (
            <div key={item.id} className={`feedback-card ${item.status}`}>
              <div className="feedback-header">
                <div className="feedback-user">
                  <strong>{item.userName}</strong>
                  <span className="feedback-email">{item.userEmail}</span>
                </div>
                <span className={`status-badge ${item.status}`}>
                  {item.status || 'new'}
                </span>
              </div>
              <div className="feedback-message">
                {item.message}
              </div>
              <div className="feedback-footer">
                <span className="feedback-time">{formatDate(item.timestamp)}</span>
                <div className="feedback-actions">
                  {item.status !== 'read' && (
                    <button 
                      className="action-btn read"
                      onClick={() => updateFeedbackStatus(item.id, 'read')}
                    >
                      Mark Read
                    </button>
                  )}
                  <button 
                    className="action-btn resolve"
                    onClick={() => updateFeedbackStatus(item.id, 'resolved')}
                  >
                    Resolve
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="no-data">No feedback received yet</p>
        )}
      </div>
    </div>
  );

  const getDaysRemaining = (recycledDate) => {
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const recycledTime = new Date(recycledDate).getTime();
    const expiryTime = recycledTime + thirtyDaysMs;
    const now = Date.now();
    const daysRemaining = Math.ceil((expiryTime - now) / (24 * 60 * 60 * 1000));
    return Math.max(0, daysRemaining);
  };

  const RecycleBinTab = () => (
    <div className="tab-content">
      <h2>Recycle Bin (30-day retention)</h2>
      <div className="feedback-grid">
        {recycledFeedback.length > 0 ? (
          recycledFeedback.map(item => (
            <div key={item.id} className="feedback-card resolved">
              <div className="feedback-header">
                <div className="feedback-user">
                  <strong>{item.userName}</strong>
                  <span className="feedback-email">{item.userEmail}</span>
                </div>
                <span className="status-badge resolved">
                  {getDaysRemaining(item.recycledAt)} days left
                </span>
              </div>
              <div className="feedback-message">
                {item.message}
              </div>
              <div className="feedback-footer">
                <span className="feedback-time">
                  Resolved: {formatDate(item.recycledAt)}
                </span>
                <div className="feedback-actions">
                  <button 
                    className="action-btn restore"
                    onClick={() => restoreFeedback(item.id)}
                  >
                    Restore
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="no-data">No recycled feedback</p>
        )}
      </div>
    </div>
  );

  if (authLoading) {
    return (
      <div className="App">
        <div className="loading">Checking authentication…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <img src="/waveact.png" alt="WaveAct Logo" className="logo-img" />
          <h1>WaveAct Admin</h1>
          <p>Please sign in to continue</p>
          <button className="google-btn" onClick={loginWithGoogle}>Sign in with Google</button>
        </div>
      </div>
    );
  }

  if (isAdmin === null) {
    return (
      <div className="App">
        <div className="loading">Checking admin permissions…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="App">
        <div className="error">
          <h2>Not authorized</h2>
          <p>Your account does not have admin access.</p>
          <button onClick={logout}>Log out</button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Error: {error}</h2>
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-title-row">
          <img src="/waveact.png" alt="WaveAct Logo" className="logo-img" />
          <h1>WaveAct Admin Dashboard</h1>
        </div>
        <div className="user-chip">
          <span>{user.email}</span>
          <button className="logout-btn" onClick={logout}>Log out</button>
        </div>
        <div className="tab-nav">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button 
            className={activeTab === 'progress' ? 'active' : ''}
            onClick={() => setActiveTab('progress')}
          >
            Progress
          </button>
          {/*
          <button 
            className={activeTab === 'learnProgress' ? 'active' : ''}
            onClick={() => setActiveTab('learnProgress')}
          >
            Learn Progress
          </button>
          */}
          <button 
            className={activeTab === 'leaderboard' ? 'active' : ''}
            onClick={() => setActiveTab('leaderboard')}
          >
            Leaderboard
          </button>
          <button 
            className={activeTab === 'displayNameChanges' ? 'active' : ''}
            onClick={() => setActiveTab('displayNameChanges')}
          >
            Name Changes
          </button>
          <button 
            className={activeTab === 'feedback' ? 'active' : ''}
            onClick={() => setActiveTab('feedback')}
          >
            Feedback
          </button>
          <button 
            className={activeTab === 'recycleBin' ? 'active' : ''}
            onClick={() => setActiveTab('recycleBin')}
          >
            Recycle Bin
          </button>
          <button 
            className={activeTab === 'admins' ? 'active' : ''}
            onClick={() => setActiveTab('admins')}
          >
            Admin Management
          </button>
        </div>
        <button className="refresh-btn" onClick={() => {fetchData(); fetchLeaderboard(); fetchDisplayNameChanges(); fetchFeedback(); fetchRecycledFeedback();}}>
          Refresh
        </button>
      </header>

      <main className="app-main">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'progress' && <ProgressTab />}
  {/* {activeTab === 'learnProgress' && <LearnProgressTab token={token} />} */}
        {activeTab === 'leaderboard' && <LeaderboardTab />}
        {activeTab === 'displayNameChanges' && <DisplayNameChangesTab />}
        {activeTab === 'feedback' && <FeedbackTab />}
        {activeTab === 'recycleBin' && <RecycleBinTab />}
        {activeTab === 'admins' && <AdminManagement />}
      </main>
    </div>
  );
}

export default App;